import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { getFeatures, getPlugins } from "./Api";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { MonthlyAnnualSelector } from './partials';

const Features = () => {

    const { lang } = useParams();
    const [monthly, setMonthly] = useState(true);

    function isDescriptive(val) {
        return typeof val === "string";
    }

    let features = getFeatures();
    let plugins = getPlugins();

    let options = {
        lite: [
            "Free forever",
            "Use on one computer",
            "All basic functionalities available (receipt printing, inventory, ...)"
        ],
        pro: [
            "Use on multiple computers within one physical store",
            "Priority support",
            "Monthly or annual subscription model",
            "Cancel anytime",
            "30 days free trial (no credit card required)"
        ]
    }

    return <>
        <HelmetProvider>
            <Helmet>
                <title>Features - Aronium</title>
                <meta name="description" content="Learn about all Aronium features in one place" />
            </Helmet>
        </HelmetProvider>

        <section className="py-8">
            <div className="max-w-screen-xl mx-auto text-center">
                <h1 className="text-4xl mb-4">Features & pricing</h1>
                <p>Find the right setup for your business</p>
            </div>
        </section>

        <MonthlyAnnualSelector monthly={monthly} setMonthly={setMonthly} />

        {/* Pricing table */}
        <section>
            <div className="px-4 mx-auto max-w-screen-xl text-center mb-12">
                <p className="mb-5 font-light text-gray-600 sm:text-xl">Aronium offers a fully functional free version you can use indefinitely, along with paid options at affordable prices to support your business grow.</p>
            </div>

            <div className="py-8 px-4 mx-auto max-w-screen-xl lg:px-6">
                <div className="space-y-8 md:grid md:grid-cols-12 sm:gap-8 md:space-y-0">

                    {/* Lite */}
                    <div className="flex flex-col md:col-span-6 bg-white justify-center p-6 rounded-lg border text-center text-gray-900 bg-white rounded-lg border  border-t-8 border-slate-300 xl:p-8">
                        <h3 className="mb-4 text-3xl font-semibold">Lite</h3>
                        <p className="font-light text-gray-500 sm:text-lg">Best for small familty shops where single cash register is all you need.</p>
                        <div className="flex justify-center items-baseline my-8">
                            <span className="mr-2 text-5xl font-extrabold">$0</span>
                        </div>
                        <ul className="mb-8 space-y-4 text-left flex-grow">
                            {
                                options.lite.map((option, index) => (
                                    <li key={index} className="flex items-center space-x-3">
                                        <svg className="flex-shrink-0 w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path></svg>
                                        <span>{option}</span>
                                    </li>
                                ))
                            }
                        </ul>
                        <a href={`/${lang}/download/lite`}
                            className="bg-green-300 no-underline px-6 py-3 inline-flex text-base font-medium text-center rounded-full text-white bg-green-500 hover:bg-green-600 transition duration-300 shadow-lg focus:ring-4 focus:ring-green-400 focus:outline-none hover:no-underline rounded-full inline-flex px-6 py-3 flex items-center justify-center">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 mr-2">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
                            </svg>
                            Download free version
                        </a>
                    </div>

                    {/* Pro */}
                    <div className="flex flex-col md:col-span-6 bg-white justify-center p-6 rounded-lg border text-center text-gray-900 bg-white rounded-lg border  border-t-8 border-slate-300 xl:p-8">
                        <h3 className="mb-4 text-3xl font-semibold">Pro</h3>
                        <p className="font-light text-gray-500 sm:text-lg">Best for growing businesses or restaurants with multiple cash registers.</p>
                        <div className="flex justify-center items-baseline my-8">
                            <span className="mr-2 text-5xl font-extrabold">{monthly ? '$10' : '$100'}</span>
                            <span className="text-gray-500">/{monthly ? 'month' : 'year'}</span>
                        </div>

                        <ul className="mb-8 space-y-4 text-left flex-grow">
                            {
                                options.pro.map((option, index) => (
                                    <li key={index} className="flex items-center space-x-3">
                                        <svg className="flex-shrink-0 w-5 h-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path></svg>
                                        <span>{option}</span>
                                    </li>
                                ))
                            }
                        </ul>

                        <a href={`/${lang}/download/pro`}
                            className="hover:no-underline text-white bg-sky-500 hover:bg-sky-600 transition duration-300 shadow-lg focus:ring-4 focus:ring-sky-400 focus:outline-none no-underline font-medium rounded-full inline-flex px-6 py-3 flex items-center justify-center">
                            <div className='flex content-center text-center'>
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 mr-2">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
                                </svg>
                                <span>Download free trial</span>
                            </div>
                        </a>
                    </div>
                </div>
            </div>
        </section>

        {/* Feature comparison */}
        <section className="my-10 max-w-screen-xl px-4 mx-auto">
            <h3 className='text-3xl font-bold text-center mb-4'>Feature comparison</h3>
            <div className='text-center'>Find list of available features along with plugin extensions</div>
            <div className="max-w-screen-xl mx-auto overflow-x-auto">
                <table className="w-full table-auto">
                    <caption className="sr-only">Feature comparison</caption>
                    <thead>
                        <tr>
                            <th></th>
                            <th scope="col">
                                <h2 className="text-2xl font-normal">Lite</h2>
                            </th>
                            <th scope="col">
                                <h2 className="text-2xl font-normal">Pro</h2>
                            </th>
                        </tr>
                    </thead>
                    <tbody className="text-center divide-y">
                        {
                            features.map((feature, index) => (
                                <tr key={index} className="h-12 md:hover:bg-gradient-to-r md:hover:from-transparent md:hover:to-transparent md:hover:via-gray-100">
                                    <td className="text-left">
                                        <span>{feature.title}</span>
                                    </td>
                                    <td>
                                        {isDescriptive(feature.lite) && feature.lite}
                                        {!isDescriptive(feature.lite) && feature.lite && <svg className="w-4 h-4 mx-auto text-green-600" clipRule="evenodd" fillRule="evenodd" fill="currentColor" stroke="currentColor" strokeLinejoin="round" strokeMiterlimit="2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="m2.25 12.321 7.27 6.491c.143.127.321.19.499.19.206 0 .41-.084.559-.249l11.23-12.501c.129-.143.192-.321.192-.5 0-.419-.338-.75-.749-.75-.206 0-.411.084-.559.249l-10.731 11.945-6.711-5.994c-.144-.127-.322-.19-.5-.19-.417 0-.75.336-.75.749 0 .206.084.412.25.56" fillRule="nonzero" /></svg>}
                                        {!isDescriptive(feature.lite) && !feature.lite && <svg className="w-5 h-5 mx-auto text-red-600" clipRule="evenodd" fillRule="evenodd" fill="currentColor" stroke="currentColor" strokeLinejoin="round" strokeMiterlimit="2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="m12 10.93 5.719-5.72c.146-.146.339-.219.531-.219.404 0 .75.324.75.749 0 .193-.073.385-.219.532l-5.72 5.719 5.719 5.719c.147.147.22.339.22.531 0 .427-.349.75-.75.75-.192 0-.385-.073-.531-.219l-5.719-5.719-5.719 5.719c-.146.146-.339.219-.531.219-.401 0-.75-.323-.75-.75 0-.192.073-.384.22-.531l5.719-5.719-5.72-5.719c-.146-.147-.219-.339-.219-.532 0-.425.346-.749.75-.749.192 0 .385.073.531.219z" /></svg>}
                                    </td>
                                    <td>
                                        {isDescriptive(feature.pro) && feature.pro}
                                        {!isDescriptive(feature.pro) && feature.pro && <svg className="w-4 h-4 mx-auto text-green-600" clipRule="evenodd" fillRule="evenodd" fill="currentColor" stroke="currentColor" strokeLinejoin="round" strokeMiterlimit="2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="m2.25 12.321 7.27 6.491c.143.127.321.19.499.19.206 0 .41-.084.559-.249l11.23-12.501c.129-.143.192-.321.192-.5 0-.419-.338-.75-.749-.75-.206 0-.411.084-.559.249l-10.731 11.945-6.711-5.994c-.144-.127-.322-.19-.5-.19-.417 0-.75.336-.75.749 0 .206.084.412.25.56" fillRule="nonzero" /></svg>}
                                    </td>
                                </tr>
                            ))
                        }
                        <tr>
                            <td colSpan="3" className="pt-4 pb-4 text-left">
                                <p className="text-2xl font-bold">Plugins</p>
                                <p className='text-gray-600 mt-4'>For additional capabilities tailored to your business needs, you can install optional plugins that extend functionality and help you get even more from your point of sale system. All plugins are available for both Aronium Lite and Aronium Pro.</p>
                                <p className='text-gray-400 text-sm mt-4 italic'>* Some plugins are offered as a paid option and may require a license. Paid plugins have their prices listed below.</p>
                            </td>
                        </tr>
                        {
                            plugins.map((plugin, index) => (
                                <tr key={index} className="h-12 md:hover:bg-gradient-to-r md:hover:from-transparent md:hover:to-transparent md:hover:via-gray-100">
                                    <td className="text-left py-4">
                                        <a href={`/${lang}/plugins/${plugin.slug}`}>{plugin.name}</a>
                                        <p className='text-gray-600 text-sm'>{plugin.description}</p>
                                    </td>
                                    <td>
                                    {plugin.price &&
                                            (
                                                <>
                                                    <p>${plugin.monthlySubscriptionAvailable && monthly ? plugin.price : plugin.price * 10}</p>
                                                    <p className='text-sm text-gray-500'> / {plugin.monthlySubscriptionAvailable && monthly ? 'month' : 'year'}</p>
                                                </>
                                            )}
                                        {!plugin.price && <svg className="w-4 h-4 mx-auto text-green-600" clipRule="evenodd" fillRule="evenodd" fill="currentColor" stroke="currentColor" strokeLinejoin="round" strokeMiterlimit="2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="m2.25 12.321 7.27 6.491c.143.127.321.19.499.19.206 0 .41-.084.559-.249l11.23-12.501c.129-.143.192-.321.192-.5 0-.419-.338-.75-.749-.75-.206 0-.411.084-.559.249l-10.731 11.945-6.711-5.994c-.144-.127-.322-.19-.5-.19-.417 0-.75.336-.75.749 0 .206.084.412.25.56" fillRule="nonzero" /></svg>}
                                    </td>
                                    <td>
                                        {plugin.price &&
                                            (
                                                <>
                                                    <p>${plugin.monthlySubscriptionAvailable && monthly ? plugin.price : plugin.price * 10}</p>
                                                    <p className='text-sm text-gray-500'> / {plugin.monthlySubscriptionAvailable && monthly ? 'month' : 'year'}</p>
                                                </>
                                            )}
                                        {!plugin.price && <svg className="w-4 h-4 mx-auto text-green-600" clipRule="evenodd" fillRule="evenodd" fill="currentColor" stroke="currentColor" strokeLinejoin="round" strokeMiterlimit="2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="m2.25 12.321 7.27 6.491c.143.127.321.19.499.19.206 0 .41-.084.559-.249l11.23-12.501c.129-.143.192-.321.192-.5 0-.419-.338-.75-.749-.75-.206 0-.411.084-.559.249l-10.731 11.945-6.711-5.994c-.144-.127-.322-.19-.5-.19-.417 0-.75.336-.75.749 0 .206.084.412.25.56" fillRule="nonzero" /></svg>}
                                    </td>
                                </tr>
                            ))
                        }
                    </tbody>
                </table>
            </div>
        </section>
    </>
}

export default Features;